import { describeVariant, tryParseMoney } from '@sitzmark/core';
import type { DeliveryLabelLine } from './delivery-labels.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Field,
  Loading,
  Modal,
  Mono,
  Screen,
} from '../../components/ui.js';
import { ItemSearch } from '../../components/item-search.js';
import { SearchSelect } from '../../components/search-select.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { NewStyleForOrderDialog } from './new-order-screen.js';
import { DeliveryReceived } from './delivery-received.js';
import type { useReceivingDocumentPrinter } from './receiving-document.js';
import './receiving.css';

/**
 * Receiving with no purchase order (SCOPE §6.1, decision 133).
 *
 * "Same screen minus the PO reference; scan/enter items, costs, quantities;
 * can create new items inline." Walk-in vendor stock, samples, what the rep
 * dropped off. Every scan is one more of that item; a count and a cost can be
 * typed; a style the shop has never stocked is created without leaving the
 * bench and its sizes appear ready to count. Committing is the same commit as
 * against an order — stock, weighted cost, the receiving document, labels.
 */

type View = Awaited<ReturnType<typeof api.purchasing.receivingOrder.query>>;
type Found = Awaited<ReturnType<typeof api.catalog.search.query>>['results'][number];

/** Choose who it came from, then count. */
export function StartWithoutOrderDialog({
  onStarted,
  onClose,
}: {
  onStarted: (receivingOrderId: string) => void;
  onClose: () => void;
}) {
  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
  });
  const [vendorId, setVendorId] = useState('');
  const start = useMutation({
    mutationFn: () => api.purchasing.startReceivingWithoutOrder.mutate({ vendorId }),
    onSuccess: (id) => {
      recordBreadcrumb('purchasing', 'started receiving without an order');
      onStarted(id);
    },
  });

  return (
    <Modal
      open
      title="Receive without an order"
      description="For stock that came with no purchase order — samples, a walk-in delivery. It is counted, costed and labeled like any other."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={start.isPending}
          disabledReason={vendorId === '' ? 'Choose who it came from' : undefined}
          onClick={() => start.mutate()}
        >
          Start counting
        </Button>
      }
    >
      <Field
        label="Vendor"
        hint="Who it came from — the receiving document and the cost belong to them."
      >
        {(props) => (
          <SearchSelect
            {...props}
            placeholder="Choose a vendor — type to search"
            value={vendorId}
            options={(taxonomy.data?.vendors ?? []).map((vendor) => ({
              value: vendor.id,
              label: vendor.name,
            }))}
            onChange={setVendorId}
          />
        )}
      </Field>
      {start.isError ? <Callout tone="danger">{errorMessage(start.error)}</Callout> : null}
    </Modal>
  );
}

export function LooseReceivingBench({
  receivingOrderId,
  canCreateStyles,
  onDone,
}: {
  receivingOrderId: string;
  /** Creating a style is a buyer's job (`catalog.manage`). */
  canCreateStyles: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const scanRef = useRef<HTMLInputElement>(null);
  const [creating, setCreating] = useState(false);
  /** Sizes of a style just created here, not yet counted. */
  const [fresh, setFresh] = useState<
    { variantId: string; description: string; sku: string; costCents: number }[]
  >([]);
  const [committed, setCommitted] = useState<{ number: string; units: number } | null>(null);

  const key = ['purchasing', 'receiving', receivingOrderId];
  const order = useQuery({
    queryKey: key,
    queryFn: () => api.purchasing.receivingOrder.query({ id: receivingOrderId }),
    staleTime: 0,
  });
  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    enabled: canCreateStyles,
  });

  const setLine = useMutation({
    mutationFn: (input: { variantId: string; quantity: number; unitCostCents?: number }) =>
      api.purchasing.setReceivedQuantity.mutate({ receivingOrderId, ...input }),
    onSuccess: (next: View, input) => {
      queryClient.setQueryData(key, next);
      setFresh((current) => current.filter((entry) => entry.variantId !== input.variantId));
    },
  });

  const commit = useMutation({
    mutationFn: () => api.purchasing.commitReceiving.mutate({ receivingOrderId }),
    onSuccess: (result) => {
      recordBreadcrumb(
        'purchasing',
        `received ${result.units} units on ${result.number}, no order`,
      );
      setCommitted({ number: result.number, units: result.units });
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
    },
  });

  useEffect(() => {
    scanRef.current?.focus();
  }, []);

  const lines = order.data?.lines ?? [];
  const countOf = (variantId: string) =>
    lines.find((line) => line.variantId === variantId)?.receivingNow ?? 0;

  /** One more of it — a scan is a unit. */
  const addOne = (variantId: string) => {
    setLine.mutate({ variantId, quantity: countOf(variantId) + 1 });
    scanRef.current?.focus();
  };

  if (committed !== null) {
    const labelLines: DeliveryLabelLine[] = lines
      .filter((line) => line.receivingNow > 0)
      .map((line) => ({
        sku: line.sku,
        barcode: line.barcode,
        productName: line.productName,
        colorName: line.colorName,
        sizeLabel: line.sizeLabel,
        altSize: line.altSize,
        received: line.receivingNow,
        retailCents: line.retailCents,
        cardPriceCents: line.cardPriceCents,
      }));
    return (
      <DeliveryReceived
        receivingOrderId={receivingOrderId}
        number={committed.number}
        units={committed.units}
        labelLines={labelLines}
        onDone={onDone}
      />
    );
  }

  const units = order.data?.totals.units ?? 0;

  return (
    <Screen
      title="Receiving without an order"
      subtitle={
        order.data === undefined
          ? undefined
          : `${order.data.receivingOrder.number} · ${order.data.receivingOrder.vendorName}`
      }
      width="wide"
      destructiveAction={
        <Button variant="ghost" onClick={onDone}>
          Leave for now
        </Button>
      }
      secondaryActions={
        canCreateStyles ? (
          <Button variant="secondary" onClick={() => setCreating(true)}>
            New model
          </Button>
        ) : undefined
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={commit.isPending}
          disabledReason={units === 0 ? 'Scan or count something in first' : undefined}
          onClick={() => commit.mutate()}
        >
          Commit · {units} unit{units === 1 ? '' : 's'}
        </Button>
      }
    >
      <ItemSearch
        inputRef={scanRef}
        label="Scan or search an item"
        placeholder="Scan or search each item as you unpack it…"
        nothingFound={(term) =>
          canCreateStyles
            ? `Nothing in the catalog matches ${term}. If it is new, add it as a new model.`
            : `Nothing in the catalog matches ${term}. A buyer can add it as a new model.`
        }
        onPick={(item) => addOne(item.variantId)}
      />

      {setLine.isError ? <Callout tone="danger">{errorMessage(setLine.error)}</Callout> : null}
      {commit.isError ? <Callout tone="danger">{errorMessage(commit.error)}</Callout> : null}

      <Card padded={false}>
        {order.isPending ? (
          <Loading label="Loading the receipt" />
        ) : lines.length === 0 && fresh.length === 0 ? (
          <p className="text-secondary receiving__empty">
            Nothing counted yet. Scan each item as it comes out of the box — each scan is one more.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Counted</th>
                  <th className="num">Cost each</th>
                  <th className="num">Extension</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.variantId} className="receiving__counted">
                    <td>
                      {line.description}
                      <div className="text-secondary">
                        <Mono>{line.sku}</Mono>
                      </div>
                    </td>
                    <td className="num">
                      <TypedNumber
                        label={`Counted, ${line.description}`}
                        value={String(line.receivingNow)}
                        pattern={/^\d{1,4}$/}
                        onCommit={(typed) =>
                          setLine.mutate({ variantId: line.variantId, quantity: Number(typed) })
                        }
                      />
                    </td>
                    <td className="num">
                      <TypedNumber
                        label={`Cost each, ${line.description}`}
                        value={(line.unitCostCents / 100).toFixed(2)}
                        money
                        onCommit={(typed) => {
                          const cost = tryParseMoney(typed);
                          if (cost === null) return;
                          setLine.mutate({
                            variantId: line.variantId,
                            quantity: line.receivingNow,
                            unitCostCents: cost,
                          });
                        }}
                      />
                    </td>
                    <td className="num money">{money(line.receivingNow * line.unitCostCents)}</td>
                  </tr>
                ))}
                {/* What to do with them, said where they appear: a reviewer
                    created a style here and "nothing happened" (2026-09-30) —
                    the sizes waited, at 0, for a count nobody told her to type. */}
                {fresh.length > 0 ? (
                  <tr>
                    <td colSpan={4} className="text-secondary">
                      New sizes: type how many came in under Counted and press Enter. Each one
                      counts once a number is in.
                    </td>
                  </tr>
                ) : null}
                {fresh.map((entry, index) => (
                  <tr key={entry.variantId}>
                    <td>
                      {entry.description} <Badge tone="info">New</Badge>
                      <div className="text-secondary">
                        <Mono>{entry.sku}</Mono>
                      </div>
                    </td>
                    <td className="num">
                      <TypedNumber
                        label={`Counted, ${entry.description}`}
                        autoFocus={index === 0}
                        value="0"
                        pattern={/^\d{1,4}$/}
                        onCommit={(typed) => {
                          if (Number(typed) > 0) {
                            setLine.mutate({ variantId: entry.variantId, quantity: Number(typed) });
                          }
                        }}
                      />
                    </td>
                    {/* The cost typed in the dialog, not a dash. */}
                    <td className="num money">{money(entry.costCents)}</td>
                    <td className="num text-secondary">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {order.data !== undefined && units > 0 ? (
        <p className="text-secondary" role="status">
          {units} unit{units === 1 ? '' : 's'} counted, {money(order.data.totals.costCents)} at
          cost. Nothing is on the shelf until you commit.
        </p>
      ) : null}

      {creating && order.data !== undefined ? (
        <NewStyleForOrderDialog
          vendorId={order.data.receivingOrder.vendorId}
          taxonomy={taxonomy.data ?? null}
          title="New model"
          description="Created like any other model — the colors × sizes below become its sizes, ready to count here."
          onClose={() => setCreating(false)}
          onCreated={(productId) => {
            setCreating(false);
            void api.products.get.query({ productId }).then((detail) => {
              setFresh(
                detail.variants.map((variant) => ({
                  variantId: variant.id,
                  sku: variant.sku,
                  costCents: variant.costCents,
                  description: describeVariant({
                    productName: detail.product.name,
                    colorName: variant.colorName,
                    sizeLabel: variant.sizeLabel,
                    altSize: variant.altSize,
                  }),
                })),
              );
            });
          }}
        />
      ) : null}
    </Screen>
  );
}

/**
 * A typed figure that commits on blur or Enter, never per keystroke
 * (decision 28) — a table re-rendering mid-count loses the operator's place.
 */
function TypedNumber({
  label,
  value,
  pattern,
  money: isMoney = false,
  autoFocus = false,
  onCommit,
}: {
  label: string;
  value: string;
  pattern?: RegExp;
  money?: boolean;
  /** Where the next thing to type is — the first new size, just created. */
  autoFocus?: boolean;
  onCommit: (typed: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const send = () => {
    const typed = draft.trim();
    if (typed === value) return;
    if (pattern !== undefined && !pattern.test(typed)) {
      setDraft(value);
      return;
    }
    onCommit(typed);
  };
  return (
    <input
      className={`input invoice__cell${isMoney ? ' money' : ''}`}
      inputMode={isMoney ? 'decimal' : 'numeric'}
      aria-label={label}
      autoFocus={autoFocus}
      // Selected on arrival, so typing replaces the 0 rather than adding to it.
      onFocus={(event) => event.currentTarget.select()}
      value={draft}
      onChange={(event) => setDraft(event.target.value.replace(isMoney ? /[^\d.]/g : /[^\d]/g, ''))}
      onBlur={send}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          send();
        }
      }}
    />
  );
}

/** Receipts that had no order: the ones still being counted, and the last few received. */
export function LooseReceipts({
  onContinue,
  printer,
}: {
  onContinue: (receivingOrderId: string) => void;
  printer: ReturnType<typeof useReceivingDocumentPrinter>;
}) {
  const receipts = useQuery({
    queryKey: ['purchasing', 'receivingOrders', 'withoutOrder'],
    queryFn: () => api.purchasing.receivingOrders.query({ withoutOrder: true, limit: 10 }),
    refetchOnMount: 'always',
  });
  const rows = receipts.data ?? [];
  if (rows.length === 0) return null;

  return (
    <Card title="Received without an order" padded={false}>
      <div className="table-scroll">
        <table className="table">
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <Mono>{row.number}</Mono>
                </td>
                <td>{row.vendorName}</td>
                <td>
                  {row.status === 'draft' ? (
                    <Badge tone="warning">being counted</Badge>
                  ) : (
                    <span className="text-secondary">
                      {row.receivedAt === null ? '' : new Date(row.receivedAt).toLocaleDateString()}
                    </span>
                  )}
                </td>
                <td className="num">
                  {row.status === 'draft' ? (
                    <Button variant="secondary" onClick={() => onContinue(row.id)}>
                      Continue
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      busy={printer.busy}
                      onClick={() => printer.print(row.id)}
                    >
                      Print receiving document
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
